/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { ApiQuery } from '@app/core/http/api-query';
import { FormGroup } from '@angular/forms';
import { Currency } from '@app/settings/currencies/currency.model';
import { CurrenciesService } from '@app/settings/currencies/currencies.service';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { CurrenciesFormService } from '@app/settings/currencies/currencies-form.service';
import { Logger } from '@app/core';

const log = new Logger('CurrencySelectComponent');

@Component({
  selector: 'app-currency-select',
  templateUrl: './currency-select.component.html',
  styleUrls: ['./currency-select.component.scss'],
})
export class CurrencySelectComponent implements OnInit {
  data: Observable<Currency[]>;
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() parentFormGroup: FormGroup;
  @Input() passFormControlName = 'currency_id';
  @Input() hideLabel = false;
  selectedCurrency: Currency = null; // използва се когато трябва да вземем цепия Currency обект от външен компонент (с #)
  input$ = new Subject<string>();
  currencyFormControlName: any = this.passFormControlName.replace('_id', '');
  lastResponse: any = null;

  constructor(private currencyService: CurrenciesService, private currencyFormService: CurrenciesFormService) {}

  ngOnInit() {
    // #69
    this.loadCurrencies();

    this.currencyFormControlName = this.passFormControlName.replace('_id', '');

    const preSelectedCurrency = this.parentFormGroup.get(this.currencyFormControlName);
    const preSelectedCurrencyId = this.parentFormGroup.get(this.passFormControlName);

    /**
     * Попълва избрания currency на едит.
     * Ако има FormControl 'currency' в parentFormGroup.
     * Ако няма ще се опита да го get-не по ID
     */
    if (preSelectedCurrency) {
      /*
       * Следи за промени в currency FormControl-a и когато намери валиден Currency спира да следи
       */
      const preselectedCurrencySubscriber = preSelectedCurrency.valueChanges.subscribe((currency: any) => {
        if (currency && currency.name) {
          this.data = of([currency]);
          this.selectedCurrency = currency;
          preselectedCurrencySubscriber.unsubscribe();
        }
      });
    } else if (preSelectedCurrencyId) {
      const preselectedCurrencyIdSubscriber = preSelectedCurrencyId.valueChanges.subscribe((currencyId: any) => {
        if (currencyId) {
          // this.loadCurrencyById(currencyId);
          this.loadCurrencies();
          preselectedCurrencyIdSubscriber.unsubscribe();
        }
      });
    }

    this.currencySearch();
  }

  loadCurrencies(keyword?: string) {
    this.loader = true;

    const query = new ApiQuery().addFilters({ active: true }).setLimit(9999).setSort('order_index');
    this.currencyService.index(query).subscribe((response: PaginatedResponse) => {
      this.data = of(response.data);
      this.loader = false;
    });
  }

  loadCurrencyById(id: number) {
    this.loader = true;
    return this.currencyService.show(id).subscribe((currency: Currency) => {
      this.data = of([currency]);
      this.selectedCurrency = currency;
      this.loader = false;
    });
  }

  currencySearch() {
    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          return this.currencyService.index(this.apiQuery.addFilters({ active: true, name: term })).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.loader = false))
          );
        })
      )
      .subscribe((currencies: PaginatedResponse) => {
        this.data = of(currencies.data);
        this.lastResponse = currencies;
        this.loader = false;
      });
  }

  change(currency: any) {
    this.selectedCurrency = currency;

    if (this.parentFormGroup.get(this.currencyFormControlName)) {
      this.parentFormGroup.get(this.currencyFormControlName).patchValue(currency);
    }
  }

  add(searchTerm: string) {
    this.currencyFormService.open(new Currency().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadCurrencyById(response.id);
        this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
      },
      (err) => {
        log.debug(err);
      }
    );
  }

  edit() {
    if (this.parentFormGroup.get(this.passFormControlName).value) {
      this.currencyFormService
        .open(new Currency().deserialize({ id: this.parentFormGroup.get(this.passFormControlName).value }))
        .then(
          (response) => {
            this.loadCurrencyById(response.id);
            this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
          },
          (err) => {
            log.error(err);
          }
        );
    }
  }
  onClear() {
    this.loadCurrencies();
  }
}
